import { type FC, useRef } from 'react';
import { Show } from '@components/Show';
import { ctxIsPDF } from '@ContractBuilder/rules/block/is-ctx';
import type { EndorsementData, EntityData } from '@ContractBuilder/types';
import { BlockActionsController } from '@features/block-actions';
import { getBlockEntityKind, isClauseBlock, isExcludedFromEndorsementPreview } from '@root/helpers/blocks';
import { useDeepCompareMemo } from '@src/hooks';
import { isBlocksPath, isClausesPath, isEndorsementViewPath } from '@utils/app-paths';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';

import { BlockContentController } from '../../block-content';
import { useBlockFlags } from '../context/context';
import { useBlockViewEdit } from '../hooks/useBlockViewEdit';
import { getSystemHelperTextForVariations } from '../utils/get-system-helper-text';

import { BlockInstructions } from './BlockInstructions';
import { VARIATIONS_COPY_MAP } from './VariationsSystemHelperTextCopy';

interface BlockViewProps {
  sectionId?: string;
  submission?: EntityData | EndorsementData;
}

const getShowHelperText = (text: string | undefined | null, isDisabled: boolean) => !isDisabled && !isEmpty(text);

export const BlockView: FC<BlockViewProps> = ({ sectionId, submission }) => {
  'use memo';

  const blockContentRef = useRef<HTMLDivElement>(null);
  const isEndorsementView = isEndorsementViewPath();
  const isBlocksPage = isBlocksPath();
  const isClausesPage = isClausesPath();

  const {
    block,
    context,
    details: {
      isEditable,
      hasVariationsConditionalLogic,
      showBlockActions,
      variations: { count },
    },
  } = useBlockFlags();
  const { id, selectedVariationId, helperText } = block;
  const isPDFRender = ctxIsPDF(context);

  const { onDelete, onEdit } = useBlockViewEdit({
    id,
    isEditable,
    name: block.name,
    type: getBlockEntityKind(block),
    sectionId,
  });

  const subHelperComponent = useDeepCompareMemo(
    () =>
      getSystemHelperTextForVariations({
        hasVariations: !!count,
        hasVariationsConditionalLogic: hasVariationsConditionalLogic,
        selectedVariationId,
        isLibraryPage: isBlocksPage || isClausesPage,
        isBlocks: isBlocksPage || !isClauseBlock(block),
        isClauses: isClausesPage || isClauseBlock(block),
      }),
    // eslint-disable-next-line -- All of those dependencies come out directly from the `block`
    [block],
  );

  const systemHelperText = VARIATIONS_COPY_MAP[subHelperComponent];
  const showHelperText = getShowHelperText(helperText || systemHelperText, isPDFRender || isEndorsementView);

  const isHiddenFromPDFPreview = isExcludedFromEndorsementPreview(block, submission);

  if (isPDFRender) {
    return (
      <>
        <BlockContentController />
        {/** fix(cb-1689): `h-*` must match the py-* class applied in `baseBlockBodyClasses` **/}
        <div className="h-3">&nbsp;</div>
      </>
    );
  }

  return (
    <div className="relative">
      <Show when={showBlockActions}>
        <BlockActionsController handleDelete={onDelete} handleEdit={onEdit} blockContentRef={blockContentRef} />
      </Show>
      <div
        ref={blockContentRef}
        className={clsx('flex flex-col', isHiddenFromPDFPreview && isEndorsementView && 'bg-gray-100')}
        onClick={onEdit}
      >
        <Show when={showHelperText}>
          <BlockInstructions
            helperText={helperText}
            subHelperText={
              <Show when={!!systemHelperText}>
                <div className="flex flex-col gap-2">
                  <div className="text-info-600 text-xs leading-normal font-semibold">{systemHelperText}</div>
                </div>
              </Show>
            }
          />
        </Show>
        <BlockContentController />
      </div>
    </div>
  );
};
